import React, { useState, useEffect } from 'react';
import { getTasks, updateTask, deleteTask, getTaskCalendarUrl, getAllCalendarUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { 
  CheckCircle, 
  Clock, 
  Trash,
  Calendar,
  Warning,
  Phone,
  EnvelopeSimple,
  Users,
  CalendarPlus,
  Export
} from '@phosphor-icons/react';
import { format, parseISO, isToday, isPast, isTomorrow } from 'date-fns';

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await getTasks();
      setTasks(response.data);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleComplete = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await updateTask(task.id, { status: newStatus });
      setTasks(prev => prev.map(t => 
        t.id === task.id ? { ...t, status: newStatus } : t
      ));
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const getFilteredTasks = () => {
    let filtered;
    switch (filter) {
      case 'pending':
        filtered = tasks.filter(t => t.status === 'pending');
        break;
      case 'completed':
        filtered = tasks.filter(t => t.status === 'completed');
        break;
      case 'overdue':
        filtered = tasks.filter(t => t.status === 'pending' && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date)));
        break;
      case 'today':
        filtered = tasks.filter(t => t.status === 'pending' && isToday(parseISO(t.due_date)));
        break;
      default:
        filtered = [...tasks];
    }
    // Always show uncompleted tasks on top
    return filtered.sort((a, b) => {
      if (a.status === 'completed' && b.status !== 'completed') return 1;
      if (a.status !== 'completed' && b.status === 'completed') return -1;
      // Within same status, sort by priority (high > medium > low) then by due date
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const pDiff = (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
      if (pDiff !== 0) return pDiff;
      return new Date(a.due_date) - new Date(b.due_date);
    });
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'high': return 'priority-high';
      case 'medium': return 'priority-medium';
      case 'low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={16} />;
      case 'email': return <EnvelopeSimple size={16} />;
      case 'meeting': return <Users size={16} />;
      default: return <CheckCircle size={16} />;
    }
  };

  const formatDueDate = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isPast(date)) return `Overdue: ${format(date, 'MMM d')}`;
    return format(date, 'MMM d, yyyy');
  };

  const getDueDateClass = (dateStr, status) => {
    if (status === 'completed') return 'text-[#86868B]';
    const date = parseISO(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-[#FF3B30] font-medium';
    if (isToday(date)) return 'text-[#FF9500] font-medium';
    return 'text-[#86868B]';
  };

  const filteredTasks = getFilteredTasks();
  const pendingCount = tasks.filter(t => t.status === 'pending').length;
  const overdueCount = tasks.filter(t => t.status === 'pending' && isPast(parseISO(t.due_date)) && !isToday(parseISO(t.due_date))).length;
  const todayCount = tasks.filter(t => t.status === 'pending' && isToday(parseISO(t.due_date))).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-entrance" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1D1D1F]">
            Tasks
          </h1>
          <p className="text-[#86868B] mt-1">
            {pendingCount} pending task{pendingCount !== 1 ? 's' : ''}
            {overdueCount > 0 && <span className="text-[#FF3B30]"> ({overdueCount} overdue)</span>}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => window.open(getAllCalendarUrl(), '_blank')}
            variant="outline"
            className="btn-secondary h-11 px-3 sm:px-4 rounded-xl text-sm"
            data-testid="export-calendar-btn"
          >
            <Export size={18} className="mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Export All to Calendar</span>
            <span className="sm:hidden">Export</span>
          </Button>
          
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 sm:w-48 h-11 rounded-xl border-gray-200" data-testid="task-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Tasks</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="today">Due Today ({todayCount})</SelectItem>
              <SelectItem value="overdue">Overdue ({overdueCount})</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <button
          onClick={() => setFilter('all')}
          className={`card-base p-4 text-left transition-all ${filter === 'all' ? 'border-[#007AFF] shadow-sm' : ''}`}
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Total</p>
          <p className="text-2xl font-bold text-[#1D1D1F] mt-1">
            {tasks.length}
          </p>
        </button>
        
        <button
          onClick={() => setFilter('pending')}
          className={`card-base p-4 text-left transition-all ${filter === 'pending' ? 'border-[#007AFF] shadow-sm' : ''}`}
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Pending</p>
          <p className="text-2xl font-bold text-[#FF9500] mt-1">
            {pendingCount}
          </p>
        </button>
        
        <button
          onClick={() => setFilter('overdue')}
          className={`card-base p-4 text-left transition-all ${filter === 'overdue' ? 'border-[#007AFF] shadow-sm' : ''}`}
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Overdue</p>
          <p className="text-2xl font-bold text-[#FF3B30] mt-1">
            {overdueCount}
          </p>
        </button>
        
        <button
          onClick={() => setFilter('completed')}
          className={`card-base p-4 text-left transition-all ${filter === 'completed' ? 'border-[#007AFF] shadow-sm' : ''}`}
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Completed</p>
          <p className="text-2xl font-bold text-[#34C759] mt-1">
            {tasks.filter(t => t.status === 'completed').length}
          </p>
        </button>
      </div>

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <div className="card-base p-12 text-center">
          <CheckCircle size={48} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-[#1D1D1F]">
            {filter === 'all' ? 'No tasks yet' : `No ${filter} tasks`}
          </h3>
          <p className="text-[#86868B] mt-2">
            Tasks will appear here when you record meetings
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTasks.map((task) => (
            <div 
              key={task.id} 
              className={`card-base p-4 transition-opacity ${
                task.status === 'completed' ? 'opacity-60' : ''
              }`}
              data-testid={`task-item-${task.id}`}
            >
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={task.status === 'completed'}
                  onCheckedChange={() => handleToggleComplete(task)}
                  className="mt-1 h-5 w-5 rounded-xl border-gray-300 data-[state=checked]:bg-[#007AFF] data-[state=checked]:border-[#007AFF] shrink-0"
                  data-testid={`task-checkbox-${task.id}`}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={`font-medium flex-1 min-w-0 ${task.status === 'completed' ? 'line-through text-[#86868B]' : 'text-[#1D1D1F]'}`}>
                      {task.title}
                    </p>
                    
                    <div className="flex items-center gap-1 shrink-0">
                      <Badge className={`${getPriorityClass(task.priority)} border rounded-xl text-xs`}>
                        {task.priority}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(getTaskCalendarUrl(task.id), '_blank')}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-[#007AFF] hover:bg-blue-50"
                        title="Add to Calendar"
                        data-testid={`calendar-task-${task.id}`}
                      >
                        <CalendarPlus size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTask(task.id)}
                        className="h-8 w-8 p-0 text-gray-400 hover:text-[#FF3B30] hover:bg-red-50"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                  
                  {task.description && (
                    <p className="text-sm text-[#86868B] mt-1 line-clamp-2">{task.description}</p>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
                    <span className="flex items-center gap-1 text-[#86868B] capitalize">
                      {getTaskTypeIcon(task.task_type)}
                      {task.task_type.replace('_', ' ')}
                    </span>
                    
                    <span className={`flex items-center gap-1 ${getDueDateClass(task.due_date, task.status)}`}>
                      {isPast(parseISO(task.due_date)) && task.status !== 'completed' && !isToday(parseISO(task.due_date)) ? (
                        <Warning size={14} />
                      ) : (
                        <Calendar size={14} />
                      )}
                      {formatDueDate(task.due_date)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}